import { useEffect, useRef } from 'react';
import { css, cx } from '@linaria/atomic';
import { TableInstance } from 'react-table';
import { theme } from '@frontend/theme';
import { IconButton, SearchField, SecondaryButton } from '../../';
import { useFormField } from '../../';
import { useDebouncedValue, useDidUpdate } from '../../../hooks';
import { SearchIcon } from '../../../icon';
import { TableCommonStyles } from '../common-styles';
import { TableData } from '../table-data-type';
import { GlobalSearchConfig } from '../table-types';

const globalFilterIconButtonStyle = css`
  margin-left: ${theme.spacing(1)};
`;

const globalFilterFullWidthStyle = css`
  width: 100%;
  display: flex;
  align-items: center;
`;

const globalFilterSizedStyle = css`
  width: 33.33%;
  margin-right: ${theme.spacing(1)};
  min-width: 200px;
  max-width: 350px;
`;

const globalFilterMarginAutoStyle = css`
  margin-right: auto;
`;

const globalFilterFlexStyle = css`
  flex: 1;
`;

const globalFilterSecondaryButtonStyle = css`
  margin-left: ${theme.spacing(0.5)};
  height: 100%;
`;

const globalFilterAfterOpacityVisibleStyle = css`
  &::after {
    opacity: 1;
  }
`;

const globalFilterAfterOpacityHiddenStyle = css`
  &::after {
    opacity: 0;
  }
`;

interface GlobalFilterComponentProps<T extends TableData> {
  tableInstance: TableInstance<T>;
  manualGlobalSearch?: boolean;
  globalSearchConfig?: GlobalSearchConfig;
  setMobileGlobalSearch: (mobileGlobalSearch: boolean) => void;
  mobileGlobalSearch: boolean;
}

export const GlobalFilterComponent = <T extends TableData>({
  tableInstance,
  manualGlobalSearch,
  globalSearchConfig,
  setMobileGlobalSearch,
  mobileGlobalSearch,
}: GlobalFilterComponentProps<T>) => {
  const { setGlobalFilter, gotoPage, isMobile, tableTrackingIds } = tableInstance;
  const notificationBadgeStyles = TableCommonStyles.filterNotificationBadgeStyles;
  const mountedRef = useRef(false);

  const globalSearch = useFormField({ type: 'text', value: globalSearchConfig?.initialValue }, [
    globalSearchConfig?.initialValue,
  ]);
  const debouncedValue = useDebouncedValue(globalSearch.value, globalSearchConfig?.debounceDelay ?? 300);
  const onSearchChange = globalSearchConfig?.onSearchChange;

  // call onSearchChange when the debouncedValue changes but not on the initial render
  useEffect(() => {
    if (onSearchChange && mountedRef.current) {
      onSearchChange(debouncedValue);
    }
    mountedRef.current = true;
  }, [debouncedValue, onSearchChange]);

  useDidUpdate(() => {
    if (manualGlobalSearch && globalSearchConfig?.searchHandler) {
      globalSearchConfig.searchHandler(debouncedValue);
    } else {
      setGlobalFilter(debouncedValue);
      gotoPage(0);
    }
  }, [debouncedValue]);

  useEffect(() => {
    if (!globalSearchConfig?.initialValue) return;

    if (globalSearchConfig?.searchHandler) {
      globalSearchConfig.searchHandler(globalSearchConfig.initialValue);
      return;
    }

    setGlobalFilter(globalSearchConfig.initialValue);
    gotoPage(0);
  }, []);

  return (
    <>
      {isMobile && !mobileGlobalSearch ? (
        <IconButton
          label='Global Table Search'
          className={cx(
            notificationBadgeStyles,
            globalFilterIconButtonStyle,
            debouncedValue.length ? globalFilterAfterOpacityVisibleStyle : globalFilterAfterOpacityHiddenStyle
          )}
          onClick={() => setMobileGlobalSearch(true)}
        >
          <SearchIcon color='light' />
        </IconButton>
      ) : (
        <div
          className={
            isMobile
              ? globalFilterFullWidthStyle
              : cx(globalFilterSizedStyle, globalSearchConfig?.position === 'left' && globalFilterMarginAutoStyle)
          }
        >
          <div className={globalFilterFlexStyle}>
            <SearchField
              placeholder={globalSearchConfig?.placeholder}
              label={globalSearchConfig?.label}
              name='Global Table Search'
              data-trackingid={tableTrackingIds.globalSearch}
              {...globalSearch}
            />
          </div>
          {isMobile && (
            <SecondaryButton
              className={globalFilterSecondaryButtonStyle}
              size='tiny'
              onClick={() => setMobileGlobalSearch(false)}
            >
              Done
            </SecondaryButton>
          )}
        </div>
      )}
    </>
  );
};
